// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { paymentTypes } from '@makemydeal/dr-offer-redux';
import { paymentMiddleware } from '@makemydeal/dr-offer-redux';
import { bootstrapActionTypes, featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import * as dealModeSelectors from '../selectors/dealModeSelectors';

// interfaces/types
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';

// utils
import type { StateTree } from '@makemydeal/dr-dash-types';
import { BALLOON } from '@makemydeal/dr-platform-types';
import { getPaymentUrl } from '../utils/paymentServiceUtils';
import { ApplicationCodes } from '@makemydeal/dr-platform-types';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import {
    CLEAR_OVERRIDES,
    UPDATE_CURRENT_CALCULATION_ENDPOINT,
    UPDATED_CREDIT_DECISION_ENABLED
} from '../actionTypes/offerActionTypes';
import * as offerActionCreators from '../actions/offerActionCreators';
import { applyProgramEndDays } from '../actions/offerActionCreators';
import { applyDealerOverrides } from '../actions/offerRedux';
import { getPaymentFeesTypes } from '../actions/paymentFeesTypesActionCreators';
import { triggerBalloonFallbackToUPQ, setBalloonFallbackHighlight } from '../actions/balloonFallbackActionCreators';
import { setCalcEngineTransitionState } from '../actions/baseDealActionCreators';
import * as baseDealSelectors from '../selectors/BaseDealSelectors';
import * as dealValidationSelectors from '../selectors/dealValidationSelectors';
import * as firstPencilSelectors from '../selectors/firstPencilSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getKeptLenderToReapply } from '../selectors/programsSelectors';
import { hasCreatedViaFirstPencil } from './firstPencilInterceptMiddleware';
import { isPaymentSequenceExemptTrigger } from './paymentSequenceMiddleware';
import { findPendingOperation } from './paymentSequenceRegistry';
import { buildPaymentPayloadHostSelectors } from '../utils/offerPayloadUtil';
import { getOriginalActionType } from '../utils/paymentActionUtils';
import { logFirstPencilDealStructureChangedPostCreate } from '../utils/firstPencilTelemetry';
import { buildUsabilityContext } from '../utils/usabilityContext';
import { trackPQPreloading } from '../utils/usabilityTelemetry';

const standardsSeedingOption = (state: StateTree, requested: unknown): { includeStandardIncentives?: boolean } =>
    featureToggleSelectors.isProgramIncentivesRestorationEnabled(state) && Boolean(requested)
        ? { includeStandardIncentives: true }
        : {};

// keep a cached version of the middleware once we're able to build it.
export let cachedPaymentReduxMiddleware:
    | ((store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => void)
    | undefined;

/** Keyed by (dealXgId, triggerActionType) — PAYMENT_RECEIVED fires on every payment-affecting edit. */
const loggedStructureChangeKeys = new Set<string>();

export const paymentMiddlewareClearCache = (): void => {
    cachedPaymentReduxMiddleware = undefined;
    loggedStructureChangeKeys.clear();
};

/**
 * Public readiness contract for other middleware (e.g. quoteModeInitialPaymentMiddleware) that
 * need to know whether a `meta.middleware.payment` action dispatched right now would actually
 * reach the real payment engine, without depending on the `cachedPaymentReduxMiddleware` internal
 * directly. Becomes true once BOOTSTRAP_SUCCESS has configured the payment middleware.
 */
export const isPaymentMiddlewareReady = (): boolean => Boolean(cachedPaymentReduxMiddleware);

const attemptBalloonFallback = (state: StateTree, store: ReduxStore<StateTree>): boolean => {
    const isBalloonEnhancementEnabled = featureToggleSelectors.isBalloonEnhancementEnabled(state);
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    if (!isBalloonEnhancementEnabled || offerType !== BALLOON) {
        return false;
    }
    const transitionState = baseDealSelectors.getCalcEngineTransitionState(state);
    if (transitionState?.balloonFallbackFinanceData) {
        store.dispatch(triggerBalloonFallbackToUPQ() as any);
        return true;
    }
    return false;
};

export const getMiddleware =
    (selectors = {} as paymentTypes.HostSelectors): ReduxMiddleware<StateTree> =>
    (store: ReduxStore<StateTree>) =>
    (next: ReduxNext) =>
    (action: AnyFSA) => {
        const state = store.getState();

        const isDealValid = dealValidationSelectors.isDealValid(state);
        if (!isDealValid) {
            next(action);
            return;
        }

        // this will only configure when all the values we need are here from the configuration.  It will not reconfigure
        // if that configuration changes (but thats not a valid use case)
        switch (action.type) {
            case initActionTypes.INIT_SUCCESS: {
                // Forward INIT_SUCCESS to reducers first so state.offer fields
                // (dealXgId, dealXgVersion, sponsor, vdpUrl, etc.) are populated
                // before downstream payment fetches read state to build payloads.
                if (cachedPaymentReduxMiddleware) {
                    cachedPaymentReduxMiddleware(store)(next)(action);
                } else {
                    next(action);
                }
                store.dispatch(applyProgramEndDays());
                store.dispatch(getPaymentFeesTypes());
                return;
            }
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                // load up the middleware
                // get what we can from state.  This typically will be the bff endpoint only since its placed as part of initial state
                const hostSelectors = buildPaymentPayloadHostSelectors({
                    ...selectors,
                    ...{
                        isFnIDealMode: dealModeSelectors.isFnIDealMode
                    }
                });

                // now we will not have the toggles, static images or the gateway in state
                // so we will have to replace them
                const paymentConfig: paymentTypes.IPaymentConfig = {
                    paymentUrl: getPaymentUrl(state),
                    selectors: hostSelectors,
                    sourceApplication: ApplicationCodes.MV
                };

                cachedPaymentReduxMiddleware = paymentMiddleware(paymentConfig);
                break;
            }
        }

        // F280192: hold pricing while the Create Deal intercept is open.
        //
        // Exempts the payment-sequence exempt triggers. Asked through `isPaymentSequenceExemptTrigger`
        // rather than the set's own `.has`, because one entry is conditional: UPDATED_SHOPPER_CONTACT is
        // exempt only for the zip-changing apply, so a name- or phone-only customer-card edit is still
        // held here (DE766084). That set nominally answers a different
        // question ("must not be discarded as superseded"), but it is a superset of what this needs and
        // hand-narrowing it lost two middleware-internal follow-ups that are never replayed:
        // APPLY_DEALER_PAYMENT_OVERRIDES (this middleware's own missingRequiredPSFields follow-up, so
        // dealer overrides were silently dropped for the session) and UPDATE_CURRENT_CALCULATION_ENDPOINT
        // (the Balloon→UPQ retry, which clears balloonFallbackFinanceData first and so escapes
        // findPendingOperation too, leaving a permanent spinner). Exempting the whole set widens the
        // AC-11 gap on paper; its other members are user-driven and unreachable behind the modal
        // backdrop, and a hang is worse than a payment that AC-11 wanted held.
        //
        // Only payment triggers are held, so the PAYMENT_RECEIVED/PAYMENT_FAILURE cleanup below still
        // runs, and this sits after the BOOTSTRAP_SUCCESS case so the engine is configured for Create.
        // paymentCallPending is deliberately left alone: it is global, and a load-time payment can
        // still be in flight.
        //
        // Cheapest discrimination first — the slice read gates `findPendingOperation`, which walks
        // every registered selector in a try/catch and would otherwise run on every payment trigger
        // for every user, toggle off included.
        if (
            action.meta?.middleware?.payment &&
            !isPaymentSequenceExemptTrigger(action) &&
            firstPencilSelectors.isPaymentSuppressedPendingCreate(state) &&
            !findPendingOperation(state)
        ) {
            next(action);
            return;
        }

        // once we have the cached version, pass the call to it
        if (cachedPaymentReduxMiddleware) {
            cachedPaymentReduxMiddleware(store)(next)(action);

            // Track PQ pre-loading fallback for usability monitoring
            if (action.meta?.missingRequiredUPQFields) {
                const currentState = store.getState();
                trackPQPreloading(
                    { offerType: offerReduxSelectors.getCurrentOfferType(currentState) },
                    // `traceId` on top of the shared five — this is the only usability event that carries it.
                    { ...buildUsabilityContext(currentState), traceId: offerReduxSelectors.getTraceId(currentState) }
                );
            }

            switch (action.type) {
                case PAYMENT_FAILURE: {
                    // Balloon Enhancement: If PQ failed for Balloon and we have saved Finance data, retry with UPQ.
                    // Check before clearing pending state to avoid false→true flicker during retry.
                    // Note: No infinite loop risk — triggerBalloonFallbackToUPQ clears balloonFallbackFinanceData
                    // before dispatching UPQ, so subsequent failures won't re-trigger the fallback.
                    const failureState = store.getState();
                    if (!attemptBalloonFallback(failureState, store)) {
                        // Fallback was not triggered — clear pending state and balloonFallbackPending flag
                        store.dispatch(offerActionCreators.updatePaymentCallPendingState(false));
                        const transitionState = baseDealSelectors.getCalcEngineTransitionState(failureState);
                        if (transitionState?.balloonFallbackPending) {
                            store.dispatch(setCalcEngineTransitionState({ ...transitionState, balloonFallbackPending: undefined }));
                        }
                    }
                    break;
                }
                case PAYMENT_RECEIVED: {
                    const state = store.getState();
                    const dealXgId = firstPencilSelectors.getLaunchDealXgId(state);

                    if (!firstPencilSelectors.isPaymentSuppressedPendingCreate(state) && hasCreatedViaFirstPencil(dealXgId)) {
                        const triggerActionType = getOriginalActionType(action);
                        if (triggerActionType !== initActionTypes.INIT_SUCCESS) {
                            const dedupeKey = `${dealXgId}:${triggerActionType}`;
                            if (!loggedStructureChangeKeys.has(dedupeKey)) {
                                loggedStructureChangeKeys.add(dedupeKey);
                                logFirstPencilDealStructureChangedPostCreate(triggerActionType);
                            }
                        }
                    }

                    const isUserProgramQuotes = offerReduxSelectors.getIsUserProgramQuotes(state);

                    const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);
                    // we want to avoid double payment call when using PQ with payment grid
                    const isPaymentGridEnabled = offerReduxSelectors.getPaymentGridToggle(state, currentOfferType);

                    // Balloon Enhancement: If PQ returned no valid payment for Balloon, retry with UPQ.
                    // Check before clearing pending state to avoid unnecessary false→true toggle.
                    if (!isUserProgramQuotes) {
                        const isPaymentInvalid = offerReduxSelectors.isMonthlyPaymentInvalid(state, BALLOON);
                        if (isPaymentInvalid && attemptBalloonFallback(state, store)) {
                            break;
                        }
                    }

                    // if we have payment received, we want to update the offer modifying state
                    store.dispatch(offerActionCreators.updatePaymentCallPendingState(false));

                    // Balloon Enhancement: If UPQ fallback succeeded with a valid payment, show the highlight/snackbar
                    if (isUserProgramQuotes && currentOfferType === BALLOON) {
                        const transitionState = baseDealSelectors.getCalcEngineTransitionState(state);
                        if (transitionState?.balloonFallbackPending) {
                            store.dispatch(setCalcEngineTransitionState({ ...transitionState, balloonFallbackPending: undefined }));
                            const isPaymentInvalid = offerReduxSelectors.isMonthlyPaymentInvalid(state, BALLOON);
                            if (!isPaymentInvalid) {
                                store.dispatch(setBalloonFallbackHighlight(true));
                            }
                        }
                    }

                    if (
                        action.meta.originalAction.type === UPDATE_CURRENT_CALCULATION_ENDPOINT &&
                        action.meta.originalAction.meta.excludeOverrides
                    ) {
                        store.dispatch({ type: CLEAR_OVERRIDES });
                    }

                    // if we are using UPQ, we can avoid this double payment call
                    if (isUserProgramQuotes || isPaymentGridEnabled) {
                        break;
                    } else if (
                        (action.meta.originalAction.type === UPDATED_CREDIT_DECISION_ENABLED &&
                            action.meta.originalAction.meta.excludeOverrides) ||
                        action.meta.originalAction.meta.missingRequiredPSFields
                    ) {
                        if (getKeptLenderToReapply(action, state)) break;
                        const dealerOverridesAction = applyDealerOverrides(
                            undefined,
                            false,
                            undefined,
                            standardsSeedingOption(state, action.meta.originalAction.meta.includeStandardIncentives)
                        );
                        store.dispatch(
                            action.meta.originalAction.meta.rebaselineUnsavedChangesWhenClean
                                ? {
                                      ...dealerOverridesAction,
                                      meta: { ...dealerOverridesAction.meta, rebaselineUnsavedChangesWhenClean: true }
                                  }
                                : dealerOverridesAction
                        );
                    }
                }
            }
        } else {
            // since the paymentRedux middleware calls next, we do not want it called twice
            // this will end up causing a middleware to be skipped
            next(action);
        }
    };
